import React, { ComponentProps, Fragment } from 'react'

import { Button, Icon } from '@atoms'

import { Select } from '../../Form'
import { LinkButton } from '../../LinkButton/LinkButton'
import { generatePagination } from './generatePagination'

export interface TablePaginationProps {
    nextPageButtonLabel: string
    lastPageButtonLabel: string
    firstPageButtonLabel: string
    previousPageButtonLabel: string
    name: string
    currentPage: number
    siblingCount?: number
    boundaryCount?: number
    totalPages: number
    onPageChange: (page: number) => void
    recordsPerPage: ComponentProps<typeof Select>['value']
    recordsPerPageOptions: ComponentProps<typeof Select>['options']
    onRecordsPerPageChange: ComponentProps<typeof Select>['onChange']
}

export const TablePagination: React.FC<TablePaginationProps> = ({
    currentPage,
    totalPages,
    onPageChange,
    recordsPerPage,
    recordsPerPageOptions,
    onRecordsPerPageChange,
    siblingCount,
    boundaryCount,
    name,
    nextPageButtonLabel,
    lastPageButtonLabel,
    firstPageButtonLabel,
    previousPageButtonLabel,
}) => {
    const isFirstPage = currentPage === 1
    const isLastPage = currentPage === totalPages

    const pageGroups = generatePagination(
        currentPage,
        totalPages,
        siblingCount,
        boundaryCount
    )

    const recordsPerPageLabel =
        recordsPerPageOptions.find((option) => option.value === recordsPerPage)
            ?.label || recordsPerPageOptions[0].label

    return (
        <div className="flex items-center gap-6 px-4 py-2">
            <Select
                verticalAlign="top"
                variant="primary"
                name={name}
                value={recordsPerPage}
                onChange={onRecordsPerPageChange}
                options={recordsPerPageOptions}
                label={recordsPerPageLabel}
                dropdownClassname={'w-paginationDropdownWidth'}
            />
            <div className="flex items-center gap-4">
                <LinkButton
                    label={firstPageButtonLabel}
                    name="first"
                    iconBefore="keyboard_double_arrow_left"
                    onClick={() => {
                        onPageChange(1)
                    }}
                    variant="secondary"
                    disabled={isFirstPage}
                />

                <LinkButton
                    label={previousPageButtonLabel}
                    name="previous"
                    variant="secondary"
                    iconBefore="chevron_left"
                    onClick={() => {
                        onPageChange(currentPage - 1)
                    }}
                    disabled={isFirstPage}
                />

                <ul className="flex items-center gap-1">
                    {pageGroups.map((group, groupIndex) => {
                        return (
                            <Fragment key={groupIndex}>
                                {group.map((page) => {
                                    return (
                                        <Fragment key={page}>
                                            <Button
                                                active={currentPage === page}
                                                name={`page-${page.toString()}`}
                                                key={page}
                                                onClick={() => {
                                                    onPageChange(page)
                                                }}
                                                size="medium"
                                                variant="ghostSecondary"
                                            >
                                                {page}
                                            </Button>
                                        </Fragment>
                                    )
                                })}
                                {groupIndex !== pageGroups.length - 1 && (
                                    <Icon
                                        className="text-neutral-800"
                                        size="medium"
                                        name="more_horiz"
                                    />
                                )}
                            </Fragment>
                        )
                    })}
                </ul>
                <LinkButton
                    label={nextPageButtonLabel}
                    name="next"
                    iconAfter="chevron_right"
                    onClick={() => {
                        onPageChange(currentPage + 1)
                    }}
                    variant="secondary"
                    disabled={isLastPage}
                />

                <LinkButton
                    label={lastPageButtonLabel}
                    name="last"
                    variant="secondary"
                    iconAfter="keyboard_double_arrow_right"
                    onClick={() => {
                        onPageChange(totalPages)
                    }}
                    disabled={isLastPage}
                />
            </div>
        </div>
    )
}
